import React, { useState } from "react";
import SelectInput from "../common/SelectInput";
import TextInput from "../common/TextInput";

type VideoSearchModalProps = {
  isOpen: boolean;
  onClose: Function;
  onSearch: Function;
  onClear: Function;
  defaultCountryName: string;
  defaultSpeciesNames: string[];
  defaultResolutionName: string;
  defaultStatusNames: string[];
  defaultTagNames: string[];
  countries: any[];
  species: any[];
  resolutions: any[];
  statuses: any[];
  tags: any[];
  subStr: any;
  onChangeSpecies: Function;
  onSelectCountry: Function;
  onChangeFilter: Function;
  onChangeResolution: Function;
  onChangeStatus: Function;
  onChangeTags: Function;
};

const VideoSearchModal = ({
  isOpen,
  onClose,
  onSearch,
  onClear,
  defaultCountryName,
  defaultSpeciesNames,
  defaultResolutionName,
  defaultStatusNames,
  defaultTagNames,
  countries,
  species,
  resolutions,
  statuses,
  tags,
  subStr,
  onChangeSpecies,
  onSelectCountry,
  onChangeFilter,
  onChangeResolution,
  onChangeStatus,
  onChangeTags,
}: VideoSearchModalProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Filter Videos</h2>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-600 transition text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              name="name"
              label="Name"
              onChange={onChangeFilter}
              value={subStr.name}
              placeholder="Search by name"
              error={null}
              disabled={false}
              className="p-2 rounded shadow w-full"
              type="text"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
              initialFocus={true}
            />

            <TextInput
              name="overview"
              label="Overview"
              onChange={onChangeFilter}
              value={subStr.overview}
              placeholder="Search by overview"
              className="p-2 rounded shadow w-full"
              error={null}
              disabled={false}
              type="text"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
              initialFocus={false}
            />

            <SelectInput
              name="country"
              label="Country"
              options={countries.map((c: any) => ({
                value: c.id,
                label: c.name,
              }))}
              onChange={(e: any) => onSelectCountry(e ? e.value : null)}
              defaultValue={
                subStr.countryObtainedId !== 0
                  ? {
                      value: subStr.countryObtainedId,
                      label: defaultCountryName,
                    }
                  : undefined
              }
              isClearable
              isSearchable
              className="w-full"
              isDisabled={false}
              isLoading={false}
              isMulti={false}
              errors={null}
            />

            <SelectInput
              name="videoResolutionId"
              label="Resolution"
              options={resolutions.map((r: any) => ({
                value: r.videoResolutionId,
                label: r.name,
              }))}
              onChange={(e: any) => onChangeResolution(e ? e.value : null)}
              defaultValue={
                subStr.videoResolutionId !== 0
                  ? {
                      value: subStr.videoResolutionId,
                      label: defaultResolutionName,
                    }
                  : undefined
              }
              isClearable
              isSearchable
              className="w-full"
              isDisabled={false}
              isLoading={false}
              isMulti={false}
              errors={null}
            />

            <SelectInput
              name="species"
              label="Species"
              options={species.map((s: any) => ({
                value: s.speciesId,
                label: s.name,
              }))}
              onChange={(e: any) =>
                onChangeSpecies(e ? e.map((species: any) => species.value) : [])
              }
              defaultValue={subStr.speciesIds.map(
                (speciesId: any, index: number) => ({
                  value: speciesId,
                  label: defaultSpeciesNames[index],
                })
              )}
              isMulti
              isClearable
              isSearchable
              className="w-full"
              isDisabled={false}
              isLoading={false}
              errors={null}
            />

            <SelectInput
              name="tags"
              className="w-full"
              isDisabled={false}
              isLoading={false}
              isMulti={true}
              errors={null}
              label="Tags"
              options={tags.map((tag: any) => ({
                value: tag.tagId,
                label: tag.name,
              }))}
              onChange={(e: any) =>
                onChangeTags(e ? e.map((tag: any) => tag.value) : [])
              }
              defaultValue={subStr.tagIds.map((tagId: any, index: number) => ({
                value: tagId,
                label: defaultTagNames[index],
              }))}
              isClearable
              isSearchable
            />

            <SelectInput
              name="status"
              label="Statuses"
              options={statuses.map((status: any) => ({
                value: status.videoStatusId,
                label: status.name,
              }))}
              onChange={(e: any) =>
                onChangeStatus(e ? e.map((status: any) => status.value) : [])
              }
              defaultValue={subStr.videoStatusIds.map(
                (statusId: any, index: number) => ({
                  value: statusId,
                  label: defaultStatusNames[index],
                })
              )}
              isMulti
              isClearable
              isSearchable
              className="w-full"
              isDisabled={false}
              isLoading={false}
              errors={null}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={() => onClear()}
            className="px-4 py-2 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-300 transition"
          >
            Reset
          </button>
          <button
            onClick={() => onSearch()}
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-tealBlue-400 hover:bg-tealBlue-600 transition"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoSearchModal;
